import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { StarHistoryEntry, ThemeName } from '@/utils/storage';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Cell,
  LabelList
} from 'recharts';
import { Star, Calendar, TrendingUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

interface StarHistoryBarGraphProps {
  starHistory: StarHistoryEntry[];
  theme: ThemeName;
}

interface ChartDataPoint {
  date: string;
  displayDate: string;
  stars: number;
  totalStarsEarned: number;
  maxPossibleStars: number;
  isPerfectDay: boolean;
  percentage: number;
}

const StarHistoryBarGraph: React.FC<StarHistoryBarGraphProps> = ({ 
  starHistory, 
  theme 
}) => {
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [starViewMode, setStarViewMode] = useState<'daily' | 'total'>('daily');

  // Get theme colors
  const getThemeColors = () => {
    switch (theme) {
      case 'robots':
        return {
          primary: '#3b82f6',
          perfect: '#10b981',
          partial: '#60a5fa',
          none: '#e5e7eb',
          text: '#1f2937'
        };
      case 'unicorn':
        return {
          primary: '#d946ef',
          perfect: '#10b981',
          partial: '#e879f9',
          none: '#f3e8ff',
          text: '#86198f'
        };
      case 'space':
        return {
          primary: '#4f46e5',
          perfect: '#10b981',
          partial: '#818cf8',
          none: '#e0e7ff',
          text: '#312e81'
        };
      case 'ocean':
        return {
          primary: '#0891b2',
          perfect: '#10b981',
          partial: '#22d3ee',
          none: '#e0f7fa',
          text: '#155e75'
        };
      case 'forest':
        return {
          primary: '#16a34a',
          perfect: '#10b981',
          partial: '#4ade80',
          none: '#dcfce7',
          text: '#166534'
        };
      case 'sunset':
        return {
          primary: '#f97316',
          perfect: '#10b981',
          partial: '#fdba74',
          none: '#ffedd5',
          text: '#9a3412'
        };
      case 'volcano':
        return {
          primary: '#dc2626',
          perfect: '#10b981',
          partial: '#f87171',
          none: '#fee2e2',
          text: '#991b1b'
        };
      case 'trucks':
        return {
          primary: '#65a30d',
          perfect: '#10b981',
          partial: '#a3e635',
          none: '#ecfccb',
          text: '#3f6212'
        };
      case 'ninja':
        return {
          primary: '#b91c1c',
          perfect: '#10b981',
          partial: '#ef4444',
          none: '#fee2e2',
          text: '#7f1d1d'
        };
      case 'spongebob':
        return {
          primary: '#eab308',
          perfect: '#10b981',
          partial: '#fde047',
          none: '#fef9c3',
          text: '#854d0e'
        };
      case 'dinosaurs':
        return {
          primary: '#15803d',
          perfect: '#047857',
          partial: '#4ade80',
          none: '#dcfce7',
          text: '#14532d'
        };
      case 'candy':
        return {
          primary: '#db2777',
          perfect: '#7c3aed',
          partial: '#f472b6',
          none: '#fce7f3',
          text: '#831843'
        };
      default:
        return {
          primary: '#3b82f6',
          perfect: '#10b981',
          partial: '#60a5fa',
          none: '#e5e7eb',
          text: '#1f2937'
        };
    }
  };

  const colors = getThemeColors();

  // Process data for chart
  const chartData = useMemo(() => {
    if (!starHistory?.length) return [];

    // Get the most recent entries, sorted by date
    const sortedHistory = [...starHistory]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // For week view, get last 7 days with offset
    if (viewMode === 'week') {
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 6 - (currentWeekOffset * 7));
      
      const weekData: ChartDataPoint[] = [];
      
      for (let i = 0; i < 7; i++) {
        const currentDate = new Date(startDate);
        currentDate.setDate(startDate.getDate() + i);
        const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        
        const entry = sortedHistory.find(h => h.date === dateString) || {
          date: dateString,
          stars: 0,
          totalStarsEarned: 0,
          maxPossibleStars: 0,
          isPerfectDay: false
        };

        const percentage = entry.maxPossibleStars > 0 
          ? (entry.stars / entry.maxPossibleStars) * 100 
          : 0;

        weekData.push({
          date: dateString,
          displayDate: currentDate.toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          }),
          stars: entry.stars,
          totalStarsEarned: entry.totalStarsEarned || 0,
          maxPossibleStars: entry.maxPossibleStars,
          isPerfectDay: entry.isPerfectDay || false,
          percentage
        });
      }
      
      return weekData;
    } 
    
    // For month view, get last 30 days
    else {
      const recentData = sortedHistory.slice(-30);
      return recentData.map(entry => {
        const date = new Date(entry.date + 'T00:00:00');
        const percentage = entry.maxPossibleStars > 0 
          ? (entry.stars / entry.maxPossibleStars) * 100 
          : 0;

        return {
          date: entry.date,
          displayDate: date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric' 
          }),
          stars: entry.stars,
          totalStarsEarned: entry.totalStarsEarned || 0,
          maxPossibleStars: entry.maxPossibleStars,
          isPerfectDay: entry.isPerfectDay || false,
          percentage
        };
      });
    }
  }, [starHistory, viewMode, currentWeekOffset, starViewMode]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartDataPoint }> }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload as ChartDataPoint;
      const displayValue = starViewMode === 'daily' ? data.stars : data.totalStarsEarned;
      const label = starViewMode === 'daily' ? 'Daily Stars' : 'Total Stars';
      
      return (
        <div className="bg-background border border-border rounded-lg p-3 shadow-lg">
          <p className="font-semibold text-foreground">{data.displayDate}</p>
          <div className="flex items-center gap-2 mt-1">
            <Star className="h-4 w-4 text-yellow-500" />
            <span className="text-sm">
              {label}: {displayValue}
              {starViewMode === 'daily' && data.maxPossibleStars > 0 && (
                <span className="text-muted-foreground">/{data.maxPossibleStars}</span>
              )}
              {starViewMode === 'daily' && data.maxPossibleStars > 0 && (
                <span className="ml-2 text-xs">({Math.round(data.percentage)}%)</span>
              )}
            </span>
          </div>
          {starViewMode === 'daily' && data.isPerfectDay && (
            <p className="text-sm text-green-600 mt-1">✨ Perfect Day!</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Get bar color - now using consistent coloring without perfect day distinction
  const getBarColor = (dataPoint: ChartDataPoint) => {
    if (starViewMode === 'total') {
      // For total stars, use a consistent color since it's cumulative
      return dataPoint.totalStarsEarned > 0 ? colors.primary : colors.none;
    }
    
    // For daily stars, use consistent color - perfect days will be indicated by star icon
    if (dataPoint.stars > 0) return colors.primary;
    return colors.none;
  };

  // Custom label component for perfect day stars
  const PerfectDayLabel = (props: {
    x?: number;
    y?: number;
    width?: number;
    payload?: { isPerfectDay?: boolean };
  }) => {
    const { x = 0, y = 0, width = 0, payload } = props;
    if (starViewMode === 'daily' && payload && payload.isPerfectDay === true) {
      return (
        <g>
          <text
            x={x + width / 2}
            y={y - 8}
            textAnchor="middle"
            fill="#fbbf24"
            fontSize="16"
            fontWeight="bold"
          >
            ⭐
          </text>
        </g>
      );
    }
    return null;
  };

  const canGoBack = currentWeekOffset < 4; // Limit to 4 weeks back
  const canGoForward = currentWeekOffset > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="w-full">
        <CardHeader className="pb-2">
          <div className="flex flex-col gap-2 overflow-x-hidden">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-5 w-5 shrink-0" />
                Star Progress
              </CardTitle>
              <CardDescription className="text-xs">
                {starViewMode === 'daily' ? 'Daily star earnings' : 'Total stars over time'}
              </CardDescription>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Star view mode toggle */}
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  onClick={() => setStarViewMode('daily')}
                  className={`px-2 py-1 text-xs transition-colors ${
                    starViewMode === 'daily' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  Daily
                </button>
                <button
                  onClick={() => setStarViewMode('total')}
                  className={`px-2 py-1 text-xs transition-colors ${
                    starViewMode === 'total' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  Total
                </button>
              </div>
              
              {/* Time range toggle */}
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  onClick={() => setViewMode('week')}
                  className={`px-2 py-1 text-xs transition-colors ${
                    viewMode === 'week' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  Week
                </button>
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-2 py-1 text-xs transition-colors ${
                    viewMode === 'month' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  Month
                </button>
              </div>
              
              {/* Week navigation */}
              {viewMode === 'week' && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setCurrentWeekOffset(currentWeekOffset + 1)}
                    disabled={!canGoBack}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setCurrentWeekOffset(currentWeekOffset - 1)}
                    disabled={!canGoForward}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {chartData.length > 0 ? (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: 20,
                    right: 10,
                    left: 0,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis 
                    dataKey="displayDate" 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey={starViewMode === 'daily' ? 'stars' : 'totalStarsEarned'} 
                    radius={[4, 4, 0, 0]}
                    strokeWidth={1}
                    stroke={colors.primary}
                  >
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={getBarColor(entry)} 
                      />
                    ))}
                    <LabelList content={<PerfectDayLabel />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No star history data available yet.</p>
                <p className="text-sm">Complete some tasks to see your progress!</p>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mt-6 text-sm">
            {starViewMode === 'daily' ? (
              <>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: colors.primary }}
                  />
                  <span>Stars Earned</span>
                </div>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: colors.none }}
                  />
                  <span>No Stars</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-yellow-500 text-lg">⭐</span>
                  <span>Perfect Day</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: colors.primary }}
                  />
                  <span>Total Stars</span>
                </div>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded" 
                    style={{ backgroundColor: colors.none }}
                  />
                  <span>No Stars Yet</span>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default StarHistoryBarGraph;